/* eslint-disable import/no-duplicates */
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { Chart } from 'react-google-charts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useCurrentEffect } from '../../../../../../lib/use-current-effect';

import { usePatient } from '../../../../../../hooks/patient';
import { getMeasurementsByDay } from '../../../../../../services/metrics';

import bpmImage from '../../../../../../assets/images/heartRateLine.svg';

import MeasurementsArea from '../../MeasurementsArea';
import { ChildrenProps, ChildrenRefProps, DataChildrenProps } from '../..';
import { MetricTypeEnum } from '../../../../../../enums/MetricTypeEnum';

const HeartRateChart: React.ForwardRefRenderFunction<
  ChildrenRefProps,
  ChildrenProps
> = ({ dateFilter }, ref) => {
  const { patient } = usePatient();

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<DataChildrenProps>();
  const [bpmGraphData, setBpmGraphData] = useState<(string | number)[][]>([
    ['00:00', 0],
  ]);

  const handleChange = useCallback(
    async (patientId?: string, isCurrent = () => true) => {
      setIsLoading(true);
      if (!patientId) {
        setIsLoading(false);
        return;
      }
      const response = await getMeasurementsByDay({
        patientId,
        metricType: MetricTypeEnum.HEART_BEAT_RATE,
        dateFilter,
      });
      if (response.length > 0) {
        const lastRecordTime = format(
          new Date(response[response.length - 1].mensuramentDate),
          "dd'/'MM'/'yyyy', ' HH'h'mm",
          { locale: ptBR },
        );

        const result = response.map(({ heartBeatRate, mensuramentDate }) => [
          format(new Date(mensuramentDate), 'HH:mm', {
            locale: ptBR,
          }),
          heartBeatRate,
        ]);

        const value = result[result.length - 1][1].toString();

        if (isCurrent()) {
          setBpmGraphData(result);
          setData({
            lastRecordTime,
            value,
          });
        }
      } else if (isCurrent()) {
        setBpmGraphData([['00:00', 0]]);
        setData(undefined);
      }

      setIsLoading(false);
    },
    [dateFilter],
  );

  useCurrentEffect(
    (isCurrent) => {
      (() => {
        if (patient?.id) {
          handleChange(patient.id, isCurrent);
        } else {
          setIsLoading(false);
        }
      })();
    },
    [dateFilter, handleChange, patient],
  );

  useImperativeHandle(ref, () => ({
    reload: () => handleChange(patient?.id),
  }));

  return (
    <MeasurementsArea
      data={{
        styleData: 'chart',
        isLoading,
        cardTitle: 'Batidas por minuto',
        dataDetails: data
          ? {
              title: 'último registro:',
              position: 'center',
              lastRecordTime: data.lastRecordTime,
              type: 'bpm',
              value: data.value,
              inAlert: false,
              image: bpmImage,
            }
          : undefined,
      }}
    >
      <Chart
        width="110%"
        height="100%"
        chartType="AreaChart"
        style={{
          marginLeft: '-10px',
        }}
        options={{
          legend: 'none',
          colors: ['#2D9CDB'],
          vAxis: {
            ticks: [60, 100, 140],
          },
          series: {
            0: { curveType: 'function' },
          },
        }}
        data={[['h', 'BPM'], ...bpmGraphData]}
        rootProps={{ 'data-testid': '2' }}
      />
    </MeasurementsArea>
  );
};

export default forwardRef(HeartRateChart);
